/**
 * Variant catalogue for Live Ball.
 *
 * Each variant supplies:
 *   - betCount + ball numbers
 *   - bet definitions (id, payout, what numbers win, colour)
 *   - layout descriptor that drives the DOM grid
 *
 * The grid descriptors are tuned to match the four reference UIs:
 *   BAL6   – split-half (1/3/5 odd | 2/4/6 even) + GANJIL/GENAP + BESAR/KECIL
 *   BAL12  – 4 color columns (Hijau/Biru/Kuning/Merah) + 1-6/7-12 + GANJIL/GENAP
 *   BAL24  – 6 cols × 4 rows alternating red(odd)/blue(even)
 *            + octets 1-8/9-16/17-24 + halves 1-12/13-24 + Biru/Merah
 *   BAL48  – 12 cols × 4 rows alternating red(odd)/blue(even)
 *            + sectors 1-16/17-32/33-48 + halves 1-24/25-48 + Biru/Merah
 *
 * Bet IDs:
 *   BAL6:  390-399 (per official spec — StraightNumber 390-395, Ganjil/Genap
 *          396/397, Besar/Kecil 398/399)
 *   BAL12: 400-419 (12 straights 400-411 + colors 412-415 + ranges 416-417 +
 *          ganjil/genap 418-419)
 *   BAL24: 420-452 (24 straights 420-443 + merah/biru 444-445 +
 *          sectors 448-450 + halves 451-452)
 *   BAL48: 453-507 (48 straights 453-500 + biru/merah 501-502 + sectors
 *          503-505 + halves 506-507)
 */
import type {
  BallVariant,
  BetDef,
  BetGridLayout,
  BetGridRow,
  GameKey,
} from '../types/game';

// ====================================================================
// BAL6
// ====================================================================
const BAL6_BETS: BetDef[] = [
  // Straights — payout 5.5x. IDs 390-395 in number order per the sheet.
  { id: 390, key: 'n1', label: '1', category: 'straight', payout: 5.5, color: 'green', winsOn: [1] },
  { id: 391, key: 'n2', label: '2', category: 'straight', payout: 5.5, color: 'blue',  winsOn: [2] },
  { id: 392, key: 'n3', label: '3', category: 'straight', payout: 5.5, color: 'green', winsOn: [3] },
  { id: 393, key: 'n4', label: '4', category: 'straight', payout: 5.5, color: 'blue',  winsOn: [4] },
  { id: 394, key: 'n5', label: '5', category: 'straight', payout: 5.5, color: 'green', winsOn: [5] },
  { id: 395, key: 'n6', label: '6', category: 'straight', payout: 5.5, color: 'blue',  winsOn: [6] },
  // Side bets — 1.95x.
  { id: 396, key: 'ganjil', label: 'GANJIL', category: 'oddEven', payout: 1.95, color: 'dark',   winsOn: [1, 3, 5] },
  { id: 397, key: 'genap',  label: 'GENAP',  category: 'oddEven', payout: 1.95, color: 'orange', winsOn: [2, 4, 6] },
  { id: 398, key: 'besar',  label: 'BESAR',  category: 'highLow', payout: 1.95, color: 'orange', winsOn: [4, 5, 6] },
  { id: 399, key: 'kecil',  label: 'KECIL',  category: 'highLow', payout: 1.95, color: 'dark',   winsOn: [1, 2, 3] },
];

const BAL6_LAYOUT: BetGridLayout = {
  // Two halves: 6 logical cols (3 left for odd-numbered, 3 right for even).
  columnsTemplate: 'repeat(2, 1fr)',
  rows: [
    { cells: [{ betKey: 'n1' }, { betKey: 'n2' }] },
    { cells: [{ betKey: 'n3' }, { betKey: 'n4' }] },
    { cells: [{ betKey: 'n5' }, { betKey: 'n6' }] },
    { cells: [{ betKey: 'ganjil' }, { betKey: 'genap' }] },
    { cells: [{ betKey: 'besar' }, { betKey: 'kecil' }] },
  ],
};

// ====================================================================
// BAL12 — 4 color columns
// ====================================================================
// Column groups (top-down 3,2,1 in screenshot — we render top-down):
//   Hijau   (green) 1,2,3      → cells stacked 3 / 2 / 1
//   Biru    (blue)  4,5,6      → 6 / 5 / 4
//   Kuning  (yellow)7,8,9      → 9 / 8 / 7
//   Merah   (red)  10,11,12    → 12 / 11 / 10
function bal12Straight(id: number, n: number, color: 'green' | 'blue' | 'yellow' | 'red'): BetDef {
  return {
    id,
    key: `n${n}`,
    label: String(n),
    category: 'straight',
    payout: 11,
    color,
    winsOn: [n],
  };
}

// Bet item IDs come from the official Google Sheet (BAL12 tab):
//   400-411 = StraightNumber1..StraightNumber12  (payout 11x)
//   412 BolaGanjil / 413 BolaGenap               (payout 1.95x)
//   414 BolaHijau / 415 BolaBiru / 416 BolaKuning / 417 BolaMerah  (payout 3.85x)
//   418 Bola1_6 / 419 Bola7_12                   (payout 1.95x)
const BAL12_BETS: BetDef[] = [
  bal12Straight(400, 1, 'green'),
  bal12Straight(401, 2, 'green'),
  bal12Straight(402, 3, 'green'),
  bal12Straight(403, 4, 'blue'),
  bal12Straight(404, 5, 'blue'),
  bal12Straight(405, 6, 'blue'),
  bal12Straight(406, 7, 'yellow'),
  bal12Straight(407, 8, 'yellow'),
  bal12Straight(408, 9, 'yellow'),
  bal12Straight(409, 10, 'red'),
  bal12Straight(410, 11, 'red'),
  bal12Straight(411, 12, 'red'),
  { id: 412, key: 'ganjil', label: 'GANJIL',        category: 'oddEven',    payout: 1.95, color: 'dark',   winsOn: [1, 3, 5, 7, 9, 11] },
  { id: 413, key: 'genap',  label: 'GENAP',         category: 'oddEven',    payout: 1.95, color: 'dark',   winsOn: [2, 4, 6, 8, 10, 12] },
  { id: 414, key: 'hijau',  label: 'Hijau (1-3)',   category: 'colorGroup', payout: 3.85, color: 'green',  winsOn: [1, 2, 3] },
  { id: 415, key: 'biru',   label: 'Biru (4-6)',    category: 'colorGroup', payout: 3.85, color: 'blue',   winsOn: [4, 5, 6] },
  { id: 416, key: 'kuning', label: 'Kuning (7-9)',  category: 'colorGroup', payout: 3.85, color: 'yellow', winsOn: [7, 8, 9] },
  { id: 417, key: 'merah',  label: 'Merah (10-12)', category: 'colorGroup', payout: 3.85, color: 'red',    winsOn: [10, 11, 12] },
  { id: 418, key: 'r1to6',  label: '1-6',           category: 'range',      payout: 1.95, color: 'dark',   winsOn: [1, 2, 3, 4, 5, 6] },
  { id: 419, key: 'r7to12', label: '7-12',          category: 'range',      payout: 1.95, color: 'dark',   winsOn: [7, 8, 9, 10, 11, 12] },
];

// Per screenshot — 4 cols, top row is the highest number in each column.
const BAL12_LAYOUT: BetGridLayout = {
  columnsTemplate: 'repeat(4, 1fr)',
  rows: [
    { cells: [{ betKey: 'n3' }, { betKey: 'n6' }, { betKey: 'n9' }, { betKey: 'n12' }] },
    { cells: [{ betKey: 'n2' }, { betKey: 'n5' }, { betKey: 'n8' }, { betKey: 'n11' }] },
    { cells: [{ betKey: 'n1' }, { betKey: 'n4' }, { betKey: 'n7' }, { betKey: 'n10' }] },
    { cells: [{ betKey: 'hijau' }, { betKey: 'biru' }, { betKey: 'kuning' }, { betKey: 'merah' }] },
    { cells: [{ betKey: 'r1to6' }, { betKey: 'ganjil' }, { betKey: 'genap' }, { betKey: 'r7to12' }] },
  ],
};

// ====================================================================
// BAL24 — odd=red, even=blue
// ====================================================================
function bal24Straight(id: number, n: number): BetDef {
  return {
    id,
    key: `n${n}`,
    label: String(n),
    category: 'straight',
    // Per Google Sheet "Odd" column: StraightNumN pays 22x (not 23x as I guessed).
    payout: 22,
    color: n % 2 === 1 ? 'red' : 'blue',
    winsOn: [n],
  };
}

function bal24Range(low: number, high: number): number[] {
  const out: number[] = [];
  for (let i = low; i <= high; i++) out.push(i);
  return out;
}

// Bet item IDs come from the official Google Sheet (BAL24 tab):
//   420-443 = StraightNum1..StraightNum24    (payout 22x)
//   444 BolaMerah / 445 BolaBiru             (payout 1.95x)
//   448 Bola1_8 / 449 Bola9_16 / 450 Bola17_24  (payout 2.9x)
//   451 Bola1_12 / 452 Bola13_24             (payout 1.95x)
// Note: 446/447 are unused.
const BAL24_BETS: BetDef[] = [
  ...Array.from({ length: 24 }, (_, i) => bal24Straight(420 + i, i + 1)),
  { id: 444, key: 'merah',   label: 'Merah', category: 'color',  payout: 1.95, color: 'red',  winsOn: bal24Range(1, 24).filter((n) => n % 2 === 1) },
  { id: 445, key: 'biru',    label: 'Biru',  category: 'color',  payout: 1.95, color: 'blue', winsOn: bal24Range(1, 24).filter((n) => n % 2 === 0) },
  { id: 448, key: 's1to8',   label: '1-8',   category: 'sector', payout: 2.9,  color: 'dark', winsOn: bal24Range(1, 8) },
  { id: 449, key: 's9to16',  label: '9-16',  category: 'sector', payout: 2.9,  color: 'dark', winsOn: bal24Range(9, 16) },
  { id: 450, key: 's17to24', label: '17-24', category: 'sector', payout: 2.9,  color: 'dark', winsOn: bal24Range(17, 24) },
  { id: 451, key: 'h1to12',  label: '1-12',  category: 'half',   payout: 1.95, color: 'dark', winsOn: bal24Range(1, 12) },
  { id: 452, key: 'h13to24', label: '13-24', category: 'half',   payout: 1.95, color: 'dark', winsOn: bal24Range(13, 24) },
];

const BAL24_LAYOUT: BetGridLayout = {
  // 24 cols of width 1; sectors span 8, halves span 12, color span 6.
  columnsTemplate: 'repeat(24, 1fr)',
  rows: [
    bal24NumRow(1, 6),
    bal24NumRow(7, 12),
    bal24NumRow(13, 18),
    bal24NumRow(19, 24),
    { cells: [
        { betKey: 's1to8',   span: 8 },
        { betKey: 's9to16',  span: 8 },
        { betKey: 's17to24', span: 8 },
      ] },
    { cells: [
        { betKey: 'h1to12',  span: 6 },
        { betKey: 'h13to24', span: 6 },
        { betKey: 'biru',    span: 6 },
        { betKey: 'merah',   span: 6 },
      ] },
  ],
};

function bal24NumRow(from: number, to: number): BetGridRow {
  const cells = [];
  for (let n = from; n <= to; n++) cells.push({ betKey: `n${n}`, span: 4 });
  return { cells };
}

// ====================================================================
// BAL48 — odd=red, even=blue
// ====================================================================
function bal48Straight(id: number, n: number): BetDef {
  return {
    id,
    key: `n${n}`,
    label: String(n),
    category: 'straight',
    // Per Google Sheet "Odd" column: StraightNumN pays 42x.
    payout: 42,
    color: n % 2 === 1 ? 'red' : 'blue',
    winsOn: [n],
  };
}

function bal48Range(low: number, high: number): number[] {
  const out: number[] = [];
  for (let i = low; i <= high; i++) out.push(i);
  return out;
}

// Bet item IDs come from the official Google Sheet (BAL48 tab):
//   453-500 = StraightNum1..StraightNum48     (payout 42x)
//   501 BolaBiru / 502 BolaMerah              (payout 1.95x)
//   503 Bola1_16 / 504 Bola17_32 / 505 Bola33_48  (payout 2.9x)
//   506 Bola1_24 / 507 Bola25_48              (payout 1.95x)
const BAL48_BETS: BetDef[] = [
  ...Array.from({ length: 48 }, (_, i) => bal48Straight(453 + i, i + 1)),
  { id: 501, key: 'biru',    label: 'Biru',  category: 'color',  payout: 1.95, color: 'blue', winsOn: bal48Range(1, 48).filter((n) => n % 2 === 0) },
  { id: 502, key: 'merah',   label: 'Merah', category: 'color',  payout: 1.95, color: 'red',  winsOn: bal48Range(1, 48).filter((n) => n % 2 === 1) },
  { id: 503, key: 's1to16',  label: '1-16',  category: 'sector', payout: 2.9,  color: 'dark', winsOn: bal48Range(1, 16) },
  { id: 504, key: 's17to32', label: '17-32', category: 'sector', payout: 2.9,  color: 'dark', winsOn: bal48Range(17, 32) },
  { id: 505, key: 's33to48', label: '33-48', category: 'sector', payout: 2.9,  color: 'dark', winsOn: bal48Range(33, 48) },
  { id: 506, key: 'h1to24',  label: '1-24',  category: 'half',   payout: 1.95, color: 'dark', winsOn: bal48Range(1, 24) },
  { id: 507, key: 'h25to48', label: '25-48', category: 'half',   payout: 1.95, color: 'dark', winsOn: bal48Range(25, 48) },
];

const BAL48_LAYOUT: BetGridLayout = {
  // 12 cols × 4 number rows + 2 footer rows.
  columnsTemplate: 'repeat(12, 1fr)',
  rows: [
    bal48NumRow(1, 12),
    bal48NumRow(13, 24),
    bal48NumRow(25, 36),
    bal48NumRow(37, 48),
    { cells: [
        { betKey: 's1to16',  span: 4 },
        { betKey: 's17to32', span: 4 },
        { betKey: 's33to48', span: 4 },
      ] },
    { cells: [
        { betKey: 'h1to24',  span: 3 },
        { betKey: 'h25to48', span: 3 },
        { betKey: 'biru',    span: 3 },
        { betKey: 'merah',   span: 3 },
      ] },
  ],
};

function bal48NumRow(from: number, to: number): BetGridRow {
  const cells = [];
  for (let n = from; n <= to; n++) cells.push({ betKey: `n${n}`, span: 1 });
  return { cells };
}

// ====================================================================
// Registry
// ====================================================================

export const VARIANTS: Record<GameKey, BallVariant> = {
  BAL6: {
    key: 'BAL6',
    ballCount: 6,
    resultsPerDraw: 1,
    bets: BAL6_BETS,
    layout: BAL6_LAYOUT,
  },
  BAL12: {
    key: 'BAL12',
    ballCount: 12,
    resultsPerDraw: 1,
    bets: BAL12_BETS,
    layout: BAL12_LAYOUT,
  },
  BAL24: {
    key: 'BAL24',
    ballCount: 24,
    resultsPerDraw: 1,
    bets: BAL24_BETS,
    layout: BAL24_LAYOUT,
  },
  BAL48: {
    key: 'BAL48',
    ballCount: 48,
    resultsPerDraw: 1,
    bets: BAL48_BETS,
    layout: BAL48_LAYOUT,
  },
};

export function getVariant(key: GameKey): BallVariant {
  return VARIANTS[key];
}

export function getBet(variant: BallVariant, key: string): BetDef | undefined {
  return variant.bets.find((b) => b.key === key);
}

export function getBetById(variant: BallVariant, id: number): BetDef | undefined {
  return variant.bets.find((b) => b.id === id);
}

/**
 * Resolve a free-text game-key URL param to a GameKey + multiplayer flag.
 * Accepts:
 *   bal6, ball6, 6, BAL6              → { variant: BAL6, multiplayer: false }
 *   BAL6M, ball6m, 6m, ball6-multi    → { variant: BAL6, multiplayer: true }
 * The "M" trails the variant number; we strip it before mapping.
 */
export function resolveGameKey(
  raw: string | null | undefined,
): { variant: GameKey; multiplayer: boolean } | null {
  if (!raw) return null;
  const k = raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  // Detect MP suffix: a trailing M after the digit (BAL6M, 6M, BALL6M, etc.).
  const mpMatch = /^(BAL|BALL)?(\d+)M$/.exec(k);
  let core = k;
  let multiplayer = false;
  if (mpMatch) {
    multiplayer = true;
    core = `BAL${mpMatch[2]}`;
  }
  let variant: GameKey | null = null;
  switch (core) {
    case '6':
    case 'BAL6':
    case 'BALL6':
      variant = 'BAL6';
      break;
    case '12':
    case 'BAL12':
    case 'BALL12':
      variant = 'BAL12';
      break;
    case '24':
    case 'BAL24':
    case 'BALL24':
      variant = 'BAL24';
      break;
    case '48':
    case 'BAL48':
    case 'BALL48':
      variant = 'BAL48';
      break;
    default:
      return null;
  }
  return { variant, multiplayer };
}

/** Server-facing key: BAL6M for multiplayer, BAL6 for single. */
export function apiGameKey(variant: GameKey, multiplayer: boolean): string {
  return multiplayer ? `${variant}M` : variant;
}

export function variantLabel(key: GameKey): string {
  switch (key) {
    case 'BAL6':  return 'Live Ball 6';
    case 'BAL12': return 'Live Ball 12';
    case 'BAL24': return 'Live Ball 24';
    case 'BAL48': return 'Live Ball 48';
  }
}
